import React, { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Table as AntTable, Space, Input } from 'antd'
import {
  PlusOutlined,
  DownOutlined,
  RightOutlined,
  SearchOutlined
} from '@ant-design/icons'
import { Wrapper, Actions, StyledButton, TableWrapper } from './ArcCampaigns.styles'
import useAction from 'utils/useAction'
import { selectors as arcCampaignsSelectors } from 'domains/arcCampaigns'
import * as actions from 'actions/views/arcCampaigns'
import { formatArcCampaignForTable } from 'domains/arcCampaigns/formatters'
import {
  openArcCampaignModal,
} from 'reducers/modals/actions'
import { getColumns } from './columns'
import ExpandedRow from './ExpandedRow'

const { Search } = Input

const ArcCampaigns = () => {
  const [filters, setFilters] = useState({})
  const [sorter, setSorter] = useState({ sort: '-created_at' })
  const [search, setSearch] = useState('')
  const [expandedRowKeys, setExpandedRowKeys] = useState([])

  const campaigns = useSelector(arcCampaignsSelectors.getArcCampaigns)
  const isLoaded = useSelector(arcCampaignsSelectors.getIsLoaded)
  const page = useSelector(arcCampaignsSelectors.getPage)
  const pageCount = useSelector(arcCampaignsSelectors.getPageCount)
  const searchString = useSelector(arcCampaignsSelectors.getSearchString)
  const isSaving = useSelector(arcCampaignsSelectors.getIsSavingActive)
  const includedData = useSelector(arcCampaignsSelectors.getIncludedData)

  const fetchCampaigns = useAction(actions.fetchArcCampaigns)
  const toggleExpandedRow = useAction(actions.toggleExpandedRow)
  const resetFilters = useAction(actions.resetFiltersAndSorter)
  const openArcCampaignModalFunc = useAction(openArcCampaignModal)

  useEffect(() => {
    fetchCampaigns({ page: 1, search: '', filters: {}, sorter: { sort: '-created_at' } })
  }, [fetchCampaigns])

  const handleTableChange = (pagination, tableFilters, tableSorter) => {
    const newPage = pagination.current
    const newFilters = tableFilters

    let newSorter = { sort: '-created_at' }
    if (tableSorter?.field) {
      const sortPrefix = tableSorter.order === 'descend' ? '-' : ''
      newSorter = { sort: `${sortPrefix}${tableSorter.field}` }
    }

    setFilters(newFilters)
    setSorter(newSorter)
    fetchCampaigns({
      page: newPage,
      search: search,
      filters: newFilters,
      sorter: newSorter
    })
  }

  const handleSearch = useCallback((value) => {
    setSearch(value)
    fetchCampaigns({
      page: 1,
      search: value,
      filters,
      sorter
    })
  }, [fetchCampaigns, filters, sorter])


  const handleEditCampaign = (campaign) => {
    openArcCampaignModalFunc({
      campaign,
      callback: () => {
        fetchCampaigns({ page, search: searchString, filters, sorter })
      }
    })
  }

  const handleCreateCampaign = () => {
    openArcCampaignModalFunc({
      callback: () => {
        fetchCampaigns({ page: 1, search: '', filters: {}, sorter })
      }
    })
  }

  const handleExpand = (expanded, record) => {
    const key = record.id
    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, key])
    } else {
      setExpandedRowKeys(expandedRowKeys.filter(k => k !== key))
    }
    toggleExpandedRow({ campaignId: key })
  }

  const columns = getColumns({
    onEdit: handleEditCampaign,
    includedData
  })

  const expandedRowRender = (campaign) => {
    return <ExpandedRow campaign={campaign} includedData={includedData} />
  }

  const dataSource = campaigns.map(campaign => formatArcCampaignForTable(campaign, includedData))

  return (
    <Wrapper>
      <Actions>
        <Space size="middle">
          <Search
            placeholder="Search campaigns by name, brand, or offer"
            onSearch={handleSearch}
            style={{ width: 400 }}
            allowClear
            enterButton={<SearchOutlined />}
          />
          <StyledButton
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateCampaign}
            loading={isSaving}
          >
            Create Campaign
          </StyledButton>
        </Space>
      </Actions>

      <TableWrapper>
        <AntTable
          columns={columns}
          dataSource={dataSource}
          loading={!isLoaded}
          onChange={handleTableChange}
          pagination={{
            current: page,
            pageSize: 20,
            total: pageCount * 20,
            showSizeChanger: false,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} campaigns`
          }}
          rowKey="id"
          expandable={{
            expandedRowRender,
            expandedRowKeys,
            onExpand: handleExpand,
            expandIcon: ({ expanded, onExpand, record }) =>
              expanded ? (
                <DownOutlined onClick={e => onExpand(record, e)} />
              ) : (
                <RightOutlined onClick={e => onExpand(record, e)} />
              )
          }}
        />
      </TableWrapper>
    </Wrapper>
  )
}

export default ArcCampaigns